import React, { useEffect, useState } from 'react';

import { Button, Card, Form } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faFileExport, faRobot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import { anchovy, blue, greyBorder, shadowLight, standardGray, white } from 'lib/css/colors';
import CategoryFilter from 'components/shared/CategoryFilter';
import { exportFileErrorSwal, exportFileSuccessSwal, SwalToast } from 'lib/utils/toast';
import {
  L4_CATEGORY_DIVISION_EXPORT_CATEGORY_PRODUCTS,
  L4_CATEGORY_DIVISION_EXPORT_LARGE_CATEGORIES,
  L4_CATEGORY_DIVISION_LARGE_CATEGORIES,
  L4_CATEGORY_DIVISION_SEND_PRODUCTS,
} from 'lib/networking/endpoints';
import L4CategorySortField from 'lib/enums/L4CategorySortField';
import LargeCategoriesTable from 'components/l4_segmentation/LargeCategoriesTable';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import SortDirection from 'lib/enums/SortDirection';
import useGet from 'lib/hooks/useGet';
import useL4SegmentationUrlParams from 'lib/hooks/useL4SegmentationUrlParams';
import usePost from 'lib/hooks/usePost';
import useTaxonomyOptions from 'lib/hooks/useTaxonomyOptions';

const DEFAULT_PAGE_SIZE = 20;

function LargeCategoriesTab() {
  const { params, replaceParams } = useL4SegmentationUrlParams();
  const [sentCategories, setSentCategories] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(() => params.mlPage);
  const [itemsPerPage, setItemsPerPage] = useState(() => params.mlPerPage || DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState(() => params.mlQ || '');
  const [debouncedSearch] = useDebounce(searchInput, 800);
  const [selectedL0Category, setSelectedL0Category] = useState(null);
  const [selectedL1Category, setSelectedL1Category] = useState(null);
  const [exportingCategoryId, setExportingCategoryId] = useState(null);
  const [sortBy, setSortBy] = useState(() => params.mlSort || L4CategorySortField.PRODUCT_COUNT);
  const [sortOrder, setSortOrder] = useState(() => params.mlOrder || SortDirection.DESC);

  const { options: taxonomyOptions, loading: taxonomyLoading } = useTaxonomyOptions(
    selectedL0Category?.label,
    selectedL1Category?.label,
    '',
    '',
    false,
    true,
  );

  useEffect(() => {
    setCurrentPage(params.mlPage);
    if (params.mlPerPage) setItemsPerPage(params.mlPerPage);
  }, [params.mlPage, params.mlPerPage]);

  useEffect(() => {
    setSearchInput(params.mlQ || '');
  }, [params.mlQ]);

  useEffect(() => {
    if ((debouncedSearch || '') !== (searchInput || '')) return;
    const q = debouncedSearch || '';
    const pq = params.mlQ || '';
    if (q === pq) return;
    setCurrentPage(1);
    replaceParams({ mlQ: debouncedSearch || null, mlPage: null });
  }, [debouncedSearch, searchInput, params.mlQ, replaceParams]);

  useEffect(() => {
    if (params.mlL0 == null) {
      setSelectedL0Category(null);
      return;
    }
    if (!taxonomyOptions.l0Options?.length) return;
    const opt = taxonomyOptions.l0Options.find(o => o.id === params.mlL0);
    if (opt) setSelectedL0Category(opt);
  }, [params.mlL0, taxonomyOptions.l0Options]);

  useEffect(() => {
    if (params.mlL1 == null) {
      setSelectedL1Category(null);
      return;
    }
    if (!taxonomyOptions.l1Options?.length) return;
    const opt = taxonomyOptions.l1Options.find(o => o.id === params.mlL1);
    if (opt) setSelectedL1Category(opt);
  }, [params.mlL1, taxonomyOptions.l1Options]);

  useEffect(() => {
    setSortBy(params.mlSort || L4CategorySortField.PRODUCT_COUNT);
    setSortOrder(params.mlOrder || SortDirection.DESC);
  }, [params.mlSort, params.mlOrder]);

  const offset = (currentPage - 1) * itemsPerPage;

  const { data: { data: categories = [], total_count: totalCount = 0 } = {}, loading } = useGet(
    L4_CATEGORY_DIVISION_LARGE_CATEGORIES,
    {
      min_products: 500,
      limit: itemsPerPage,
      offset: offset,
      search: debouncedSearch,
      l0_category_id: selectedL0Category?.id,
      l1_category_id: selectedL1Category?.id,
      sort_by: sortBy,
      sort_order: sortOrder,
    },
  );

  const handleSort = field => {
    let nextSortBy = field;
    let nextOrder = SortDirection.DESC;
    if (sortBy === field) {
      nextOrder = sortOrder === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
      nextSortBy = sortBy;
    } else {
      nextSortBy = field;
      nextOrder = SortDirection.DESC;
    }
    setSortBy(nextSortBy);
    setSortOrder(nextOrder);
    setCurrentPage(1);
    replaceParams({
      mlSort: nextSortBy === L4CategorySortField.PRODUCT_COUNT ? null : nextSortBy,
      mlOrder: nextOrder === SortDirection.DESC ? null : nextOrder,
      mlPage: null,
    });
  };

  const { postData: sendToML, loading: sendingToML } = usePost(L4_CATEGORY_DIVISION_SEND_PRODUCTS);
  const { postData: exportLargeCategories, loading: exporting } = usePost(
    L4_CATEGORY_DIVISION_EXPORT_LARGE_CATEGORIES,
    () => exportFileSuccessSwal('The export csv file will be generated in a while.'),
    () => exportFileErrorSwal(),
  );

  const { postData: exportCategoryProducts } = usePost(
    L4_CATEGORY_DIVISION_EXPORT_CATEGORY_PRODUCTS,
    () => {
      setExportingCategoryId(null);
      exportFileSuccessSwal('The export csv file will be generated in a while.');
    },
    () => {
      setExportingCategoryId(null);
      exportFileErrorSwal();
    },
  );

  const handleExportProducts = l4CategoryId => {
    setExportingCategoryId(l4CategoryId);
    exportCategoryProducts({ l4_category_id: l4CategoryId });
  };

  const handleSendToML = async l4CategoryId => {
    try {
      const response = await sendToML({ l4_category_id: l4CategoryId });
      if (response?.error) {
        throw response.error;
      }
      setSentCategories(prev => new Set([...prev, l4CategoryId]));
      SwalToast('success', 'Successfully started AI segmentation processing');
    } catch {
      SwalToast('error', 'Failed to start AI segmentation. Please try again.');
    }
  };

  const handlePageSizeChange = newSize => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    replaceParams({
      mlPerPage: newSize === DEFAULT_PAGE_SIZE ? null : newSize,
      mlPage: null,
    });
  };

  const handleMlPageChange = page => {
    setCurrentPage(page);
    replaceParams({ mlPage: page <= 1 ? null : page });
  };

  const handleL0Change = (option, triggeredAction) => {
    if (triggeredAction.action === 'clear') {
      setSelectedL0Category(null);
      replaceParams({ mlL0: null, mlL1: null, mlPage: null });
    } else {
      setSelectedL0Category(option);
      replaceParams({ mlL0: option?.id ?? null, mlL1: null, mlPage: null });
    }
    setSelectedL1Category(null);
    setCurrentPage(1);
  };

  const handleL1Change = (option, triggeredAction) => {
    if (triggeredAction.action === 'clear') {
      setSelectedL1Category(null);
      replaceParams({ mlL1: null, mlPage: null });
    } else {
      setSelectedL1Category(option);
      replaceParams({ mlL1: option?.id ?? null, mlPage: null });
    }
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSelectedL0Category(null);
    setSelectedL1Category(null);
    setCurrentPage(1);
    replaceParams({ mlQ: null, mlL0: null, mlL1: null, mlPage: null });
  };

  const hasActiveFilters = debouncedSearch || selectedL0Category || selectedL1Category;

  const renderTableContent = () => {
    if (loading) {
      return (
        <Card.Body className={css(styles.loadingContainer)}>
          <LoadingSpinner />
        </Card.Body>
      );
    }

    if (totalCount === 0) {
      return (
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon={faRobot} size="3x" className={css(styles.emptyIcon)} />
          <h5 className="mt-3 mb-2">
            {hasActiveFilters ? 'No Matching Categories' : 'No Large Categories Found'}
          </h5>
          <p className={css(styles.emptyText)}>
            {hasActiveFilters
              ? 'No L4 categories match your current filters.'
              : 'There are no L4 categories with 500+ products available for AI/ML processing.'}
          </p>
          {hasActiveFilters && (
            <Button variant="outline-primary" size="sm" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </Card.Body>
      );
    }

    return (
      <Card.Body className={css(styles.cardBody)}>
        <div className={css(styles.tableWrapper)}>
          <LargeCategoriesTable
            categories={categories}
            sendingToML={sendingToML}
            sentCategories={sentCategories}
            onSendToML={handleSendToML}
            onExportProducts={handleExportProducts}
            exportingCategoryId={exportingCategoryId}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      </Card.Body>
    );
  };

  return (
    <div className={css(styles.container)}>
      <Card className={css(styles.card)}>
        <Card.Header className={css(styles.cardHeader)}>
          <div className={css(styles.headerTop)}>
            <div className={css(styles.headerLeft)}>
              <FontAwesomeIcon icon={faRobot} className={css(styles.headerIcon)} />
              <span className={css(styles.headerTitle)}>Large L4 Categories (500+ Products)</span>
            </div>
            <div className={css(styles.headerRight)}>
              <span className={css(styles.totalCount)}>
                Total: <strong>{totalCount.toLocaleString()}</strong> categories
              </span>
              {totalCount > itemsPerPage && (
                <PaginationComponent
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={handlePageSizeChange}
                  currentPage={currentPage}
                  setCurrentPage={handleMlPageChange}
                  disabled={loading}
                  defaultSize
                />
              )}
            </div>
          </div>
          <div className={css(styles.filtersRow)}>
            <div className={css(styles.filterGroup)}>
              <div className={css(styles.filterSelect)}>
                <CategoryFilter
                  categoryOptions={taxonomyOptions.l0Options}
                  selectedCategoryName={selectedL0Category?.label}
                  onChange={handleL0Change}
                  placeholder="Filter by L0..."
                  loading={taxonomyLoading}
                />
              </div>
              <div className={css(styles.filterSelect)}>
                <CategoryFilter
                  categoryOptions={taxonomyOptions.l1Options}
                  selectedCategoryName={selectedL1Category?.label}
                  onChange={handleL1Change}
                  placeholder="Filter by L1..."
                  loading={taxonomyLoading}
                />
              </div>
              <Form.Control
                type="text"
                placeholder="Search L4 category..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className={css(styles.searchInput)}
              />
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => exportLargeCategories({})}
              disabled={exporting}
              className={css(styles.exportButton)}
            >
              {exporting ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                  Export all (500+)
                </>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearFilters}
                className={css(styles.clearAllButton)}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </Card.Header>
        {renderTableContent()}
      </Card>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: '8px 0',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '12px',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  filterSelect: {
    minWidth: '250px',
  },
  searchInput: {
    minWidth: '220px',
    fontSize: '13px',
    height: '38px',
  },
  exportButton: {
    whiteSpace: 'nowrap',
    height: '38px',
  },
  clearAllButton: {
    whiteSpace: 'nowrap',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  card: {
    border: `1px solid ${greyBorder}`,
    borderRadius: '8px',
    boxShadow: `0 2px 8px ${shadowLight}`,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: white,
    borderBottom: `1px solid ${greyBorder}`,
    padding: '12px 20px',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    color: blue,
    fontSize: '18px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: anchovy,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  totalCount: {
    fontSize: '14px',
    color: anchovy,
  },
  cardBody: {
    padding: 0,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  emptyCard: {
    border: `1px solid ${greyBorder}`,
    borderRadius: '8px',
    marginTop: '8px',
  },
  emptyIcon: {
    color: standardGray,
    opacity: 0.5,
  },
  emptyText: {
    color: standardGray,
    fontSize: '14px',
    marginBottom: 0,
  },
});

export default LargeCategoriesTab;
